import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { FormField } from '~/components/system/form-field';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { usePriceTypeMutations } from '~/hooks/use-price-types';
import { priceTypeSchema, EMPTY_PRICE_TYPE, type PriceTypeFormValues } from '~/lib/schemas/price-type';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	initial: PriceTypeFormValues | null;
}

const TOGGLES = [
	{ field: 'base', labelKey: 'prices.priceTypes.columns.base' },
	{ field: 'tax', labelKey: 'prices.priceTypes.columns.tax' },
	{ field: 'iv', labelKey: 'prices.priceTypes.columns.visible' }
] as const;

/** Add / edit a price type (Είδος Τιμής). */
export function PriceTypeEditDialog({ open, onOpenChange, initial }: Props) {
	const { t } = useTranslation();
	const { create, update } = usePriceTypeMutations();
	const form = useForm<PriceTypeFormValues>({
		resolver: zodResolver(priceTypeSchema) as Resolver<PriceTypeFormValues>,
		defaultValues: initial ?? EMPTY_PRICE_TYPE,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, watch, formState } = form;
	const isEdit = Boolean(initial?.id);

	useEffect(() => {
		if (open) reset(initial ?? EMPTY_PRICE_TYPE);
	}, [open, initial, reset]);

	const onSubmit = handleSubmit(async values => {
		try {
			if (values.id) await update.mutateAsync(values);
			else await create.mutateAsync(values);
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{isEdit ? t('prices.priceTypes.editTitle') : t('prices.priceTypes.addTitle')}</DialogTitle>
					<DialogDescription>{t('prices.priceTypes.formSubtitle')}</DialogDescription>
				</DialogHeader>
				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					<FormField label={t('prices.priceTypes.columns.name')} error={formState.errors.nm?.message}>
						<Input {...register('nm')} maxLength={100} aria-invalid={Boolean(formState.errors.nm)} autoFocus />
					</FormField>
					<div className="grid grid-cols-3 gap-3">
						{TOGGLES.map(({ field, labelKey }) => (
							<div key={field} className="flex items-center justify-between rounded-md border border-border px-3 py-2">
								<Label htmlFor={`pt-${field}`}>{t(labelKey)}</Label>
								<Switch id={`pt-${field}`} checked={watch(field)} onCheckedChange={next => setValue(field, next, { shouldDirty: true })} />
							</div>
						))}
					</div>
					<FormField label={t('prices.priceTypes.columns.sort')} error={formState.errors.si?.message}>
						<Input type="number" {...register('si', { valueAsNumber: true })} aria-invalid={Boolean(formState.errors.si)} />
					</FormField>
					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={create.isPending || update.isPending}>
							{create.isPending || update.isPending ? t('common.saving') : t('common.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
